<template>
  <section class="w-full overflow-hidden">
    <div class="container z-grid v-page-section-container-padding">
      <!--
        `w-full` bounds this slider: a grid item wrapping a flex track with no width of its own lets
        Chromium's intrinsic sizing feed back on itself and runs the slides out to 2^24 px. Only a
        percentage width stops that, a `min-width` does not.

        `watch-slides-progress` sets `.swiper-slide-visible`, which `carousel--redesign` in
        sliders.scss needs to lift a slide out of the dimmed state it gives every slide.

        DiscoveryCarousel.vue is the other slider styled this way and needs the same pair.
      -->
      <Swiper
        :slides-per-view="1"
        :speed="500"
        :watch-slides-progress="true"
        class="carousel--redesign relative mb-0 w-full min-w-0 col-span-10 col-start-2"
        @swiper="onSwiper"
        @lock="isLocked = true"
        @unlock="isLocked = false"
      >
        <SwiperSlide v-for="(image, i) in items" :key="`img${i}`" class="rounded-[20px] overflow-hidden">
          <MoleculePicture v-if="image" :image="image" :lazy="false" :aria-hidden="true" />
          <div v-if="!hideCaptions[0]" class="px-6 py-3 text-xs leading-tight bg-white w-full md:px-10 md:py-10 md:text-base" v-html="image.title"></div>
        </SwiperSlide>
        <!--
          Swiper's Navigation module only builds its own buttons when `createElements` is set and the
          params carry `auto: true`, so a bare `navigation: true` gives a slider nobody can operate.
          These are real elements driven off the instance instead, shown from `lg` (1024px) up, and
          hidden while Swiper reports the slider locked, meaning every slide already fits.
        -->
        <button v-show="!isLocked" type="button" class="swiper-button-prev hidden lg:flex" :aria-label="t_('previous')" @click="swiper?.slidePrev()">
          <AtomIconChevronLeft class="w-full h-full" />
        </button>
        <button v-show="!isLocked" type="button" class="swiper-button-next hidden lg:flex" :aria-label="t_('next')" @click="swiper?.slideNext()">
          <AtomIconChevronRight class="w-full h-full" />
        </button>
      </Swiper>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Swiper as SwiperClass } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
// Only for the base .swiper-button-prev/-next positioning; the Navigation module itself isn't used.
import 'swiper/css/navigation'

defineProps({
  items: { type: Array as PropType<any[]>, required: true },
  hideCaptions: { type: Array as PropType<any[]>, required: false, default: () => [] },
})

const { t_ } = setupTranslationPrefixer('MatrixCarousel')

// shallowRef, not ref: a deep ref proxies the whole Swiper instance and unwraps its internals, which
// both costs needless reactivity on a third-party object and breaks its own methods' `this`.
const swiper = shallowRef<SwiperClass | null>(null)

// Swiper's `watchOverflow` decides this per breakpoint and keeps it current on resize; the template
// hides the arrows on it. Seeded from the instance because `lock` has already fired by then when the
// slider starts out locked.
const isLocked = ref(false)

function onSwiper(swiperInstance: SwiperClass) {
  swiper.value = swiperInstance
  isLocked.value = swiperInstance.isLocked
}
</script>
